import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import ScrollReveal from "./ScrollReveal";

const ContactSection = () => {
  const { t } = useTranslation();

  const [application, setApplication] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  useEffect(() => {
    const storedApp = localStorage.getItem("userApplication") || "";
    setApplication(storedApp);
  }, []);

  const translateBackend = (data) => {
    if (!data) return t("msg_server_or_network");
    if (data.message_key) return t(data.message_key, data.message_params || {});
    if (Array.isArray(data.errors) && data.errors.length) {
      const first = data.errors[0];
      if (first?.key) return t(first.key, first.params || {});
      if (typeof first === "string") return t(first);
    }
    return t("msg_unknown_error");
  };

  const validate = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      setErrorMsg(t("contact_all_required"));
      return false;
    }

    if (!emailRegex.test(email.trim())) {
      setErrorMsg(t("msg_invalid_email"));
      return false;
    }

    if (message.trim().length < 10) {
      setErrorMsg(t("contact_message_short"));
      return false;
    }

    setErrorMsg("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setInfoMsg("");
    setErrorMsg("");

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/contact`, {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
        application: (application || "").trim().toLowerCase(),
      });

      if (res?.data?.success) {
        setInfoMsg(translateBackend(res?.data));
        setName("");
        setEmail("");
        setSubject("");
        setMessage("");
      } else {
        setErrorMsg(translateBackend(res?.data));
      }
    } catch (err) {
      setErrorMsg(translateBackend(err?.response?.data));
    } finally {
      setLoading(false);
    }
  };

  const msgClass = errorMsg
    ? "assist-message assist-message-error"
    : "assist-message assist-message-info";

  return (
    <section className="assist-section assist-section-contact">
      <div className="assist-container">
        <ScrollReveal className="assist-section-head">
          <p className="assist-section-eyebrow">{t("contact_title", "Contact")}</p>
          <h2 className="assist-section-title">
            {t("contact_title", "Parlez-nous de votre besoin")}
          </h2>
          <p className="assist-section-text">
            {t(
              "contact_subtitle",
              "Envoyez-nous un message et nous vous répondrons dès que possible."
            )}
          </p>
        </ScrollReveal>

        <ScrollReveal delay={120}>
          <div className="assist-card assist-contact-card">
            <form
              className="assist-contact-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (!loading) handleSubmit();
              }}
              noValidate
            >
              <div className="assist-contact-row">
                <input
                  type="text"
                  className="assist-input"
                  placeholder={t("contact_name", "Votre nom")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
                <input
                  type="email"
                  className="assist-input"
                  placeholder={t("contact_email", "Votre email")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>

              <input
                type="text"
                className="assist-input"
                placeholder={t("contact_subject", "Sujet")}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />

              <textarea
                className="assist-input resize-y"
                placeholder={t("contact_message_placeholder", "Écrivez votre message...")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
              />

              {(errorMsg || infoMsg) && <p className={msgClass}>{errorMsg || infoMsg}</p>}

              <div className="assist-contact-actions">
                <button type="submit" disabled={loading} className="assist-btn-primary">
                  {loading ? t("contact_sending", "Envoi...") : t("contact_send", "Envoyer")}
                </button>

                <button
                  type="button"
                  className="assist-btn-secondary"
                  onClick={() => {
                    setName("");
                    setEmail("");
                    setSubject("");
                    setMessage("");
                    setErrorMsg("");
                    setInfoMsg("");
                  }}
                  disabled={loading}
                >
                  {t("contact_reset", "Réinitialiser")}
                </button>
              </div>

              <div className="assist-contact-direct">
                <p className="assist-contact-direct-title">
                  {t("contact_direct_title", "Ou contactez-nous directement")}
                </p>

                <div className="assist-contact-direct-grid">
                  <a
                    href="mailto:ehsan.sgooya@assist-by-scan.com"
                    className="assist-contact-link-card"
                  >
                    <span className="assist-contact-link-label">
                      {t("contact_email_label", "Email")}
                    </span>
                    <span className="assist-contact-link-value">
                      ehsan.sgooya@assist-by-scan.com
                    </span>
                  </a>

                  <a href="tel:+33123456789" className="assist-contact-link-card">
                    <span className="assist-contact-link-label">
                      {t("contact_phone_label", "Téléphone")}
                    </span>
                    <span className="assist-contact-link-value">+33 0 00 00 00 00</span>
                  </a>
                </div>
              </div>
            </form>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default ContactSection;
